import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation, faExclamationCircle, faSquareArrowUpRight } from '@fortawesome/pro-solid-svg-icons';
import { createRethinkCallListSync } from '../../../helpers/prospect_api';
import { type RethinkCallListSync } from 'bundles/connect/types';
import { fetchCallListExportCreditCounts } from '../../../helpers/prospect_api';

type Props = {
  callListId: number,
  initialCallListName: string,
  show: boolean,
  onExportStarted: (sync: RethinkCallListSync) => void,
  onHide: () => void
}

type ExportCreditCounts = {
  availableCredits: number;
  creditsForExport: number;
  callListSize: number;
};

export default function RethinkSyncModalStart(
  { callListId, initialCallListName, show, onExportStarted, onHide }: Props
) {
  const CALL_LIST_SYNC_PROPERTY_LIMIT = 1000;
  const [includeContacts, setIncludeContacts] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [callListName, setCallListName] = useState(initialCallListName);
  const [exportCreditCounts, setExportCreditCounts] = useState<ExportCreditCounts>();

  useEffect(() => {
    if (!show) return;

    fetchCallListExportCreditCounts(callListId).then(setExportCreditCounts);
  }, [show]);

  const showPropertyLimitWarning = exportCreditCounts &&
                                   exportCreditCounts.callListSize > CALL_LIST_SYNC_PROPERTY_LIMIT;

  const showCreditWarning = exportCreditCounts &&
                            exportCreditCounts.availableCredits < exportCreditCounts.creditsForExport &&
                            includeContacts;

  const showCreditInfo = exportCreditCounts && exportCreditCounts.creditsForExport > 0 &&
                         exportCreditCounts.availableCredits >= exportCreditCounts.creditsForExport &&
                         includeContacts;

  const handleSync = () => {
    createRethinkCallListSync({
      call_list_id: callListId,
      include_contacts: includeContacts,
      send_finished_email: sendEmail,
      rethink_list_name_override: callListName
    }).then((sync: RethinkCallListSync) =>{
      onExportStarted(sync);
    });
  };

  return (
    <>
      <Modal.Header closeButton>
        <h5 className="mb-0">Export List To Rethink</h5>
      </Modal.Header>
      <Modal.Body>
        {showPropertyLimitWarning && (
          <Alert className="p-3" variant="warning">
            <div className="d-flex align-items-start gap-2">
              <FontAwesomeIcon className="py-1" icon={faCircleExclamation} />
              <div className="d-flex flex-column">
                <div className="fw-bolder">
                  Warning
                </div>
                <div>
                  Rethink exports are limited to a maximum of 1,000 properties.
                  Only the first 1,000 will be exported.
                </div>
              </div>
            </div>
          </Alert>
        )}
        {showCreditWarning && (
          <Alert className="p-3" variant="danger">
            <div className="d-flex align-items-start gap-2">
              <FontAwesomeIcon className="py-1" icon={faCircleExclamation} />
              <div className="d-flex flex-column">
                <div className="fw-bolder">
                  Not Enough Credits
                </div>
                <div>
                  This list contains {exportCreditCounts.creditsForExport} properties
                  whose owners have not been unmasked.
                  Owners for these properties will not be included due to lack of credits.
                </div>
                <div>You currently have {exportCreditCounts.availableCredits} credits available.</div>
              </div>
            </div>
          </Alert>
        )}
        {showCreditInfo && (
          <Alert className="p-3" variant="info">
            <div className="d-flex align-items-start gap-2">
              <FontAwesomeIcon className="mt-1" icon={faExclamationCircle} />
              <div className="d-flex flex-column">
                <div className="fw-bolder">
                  Credits will be used
                </div>
                <div>
                  The list selected contains owners that have not been unmasked.
                  Running this export will use up to <strong>{exportCreditCounts.creditsForExport} credits.</strong>
                </div>
                <div>You currently have {exportCreditCounts.availableCredits} credits available.</div>
              </div>
            </div>
          </Alert>
        )}
        <div className="d-flex flex-column gap-3">
          <Form.Group>
            <Form.Label>List Name</Form.Label>
            <Form.Control
              placeholder="List Name"
              value={callListName}
              onChange={e => setCallListName(e.target.value)}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label className="fs-large">Export Options</Form.Label>
            <Form.Check
              checked={includeContacts}
              label={
                <div>
                  <div>Include owner contact data</div>
                  <small className="text-muted lh-sm">
                    This will expend an additional credit for any owner
                    contact that has not been unmasked previously.
                  </small>
                </div>
              }
              type="checkbox"
              onChange={e => setIncludeContacts(e.target.checked)}
            />
            <Form.Check
              checked={sendEmail}
              label="Email me when export is completed"
              type="checkbox"
              onChange={e => setSendEmail(e.target.checked)}
            />
          </Form.Group>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="link" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="secondary" onClick={handleSync}>
          <FontAwesomeIcon icon={faSquareArrowUpRight}/>
          Begin Export
        </Button>
      </Modal.Footer>
    </>
  );
}
